// Phase-25.C — Strict category picker for the Recommend page (and Simulator).
//
// Replaces the old `WhereCombobox` on the Recommend page. Differences from
// the Rule Builder picker:
//   • Single-select (Recommend evaluates ONE category at a time).
//   • No "Create new" affordance — consumers can't extend the vocabulary,
//     only admins can.
//   • Bound to wallet-aware filtering: only categories that at least one
//     of the user's cards has a rule for are shown by default. A toggle
//     reveals the full canonical list ("Show all").
//   • Default selection is 'General' (sort_order=1 in the registry).
//
// Why we drop the free-text combobox: the locked design treats Recommend as
// a strictly category-driven surface. Merchant/subcategory questions are now
// handled by RefinementPanel after a category is picked, not before.
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { useCategories } from '@/hooks/useCategories';

const _norm = (s) => String(s || '').trim().toLowerCase();

export function CategoryPicker({
  value,
  onChange,
  walletCategories = [],         // categories that at least one wallet card has a rule for
  testid = 'recommend-category-picker',
}) {
  const { categories, loading } = useCategories();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Wallet-aware filtering: by default, only categories that have at least
  // one matching rule in the wallet are shown. The "Show all" toggle
  // reveals the full canonical list (useful for exploratory queries).
  // When the wallet is empty (guest, or no cards yet), we fall back to
  // showing the full list so the page isn't broken on first load.
  const hasWallet = walletCategories && walletCategories.length > 0;
  const [showAll, setShowAll] = useState(!hasWallet);

  const walletSet = useMemo(() => {
    const out = new Set();
    for (const w of walletCategories) {
      if (w) out.add(_norm(w));
    }
    return out;
  }, [walletCategories]);

  const filtered = useMemo(() => {
    const active = (categories || []).filter((c) => c.is_active !== false);
    const wallet = showAll ? active : active.filter((c) => walletSet.has(_norm(c.value)));
    const q = _norm(search);
    return q
      ? wallet.filter((c) => {
          const hay = _norm(`${c.value} ${c.label || ''} ${(c.aliases || []).join(' ')}`);
          return hay.includes(q);
        })
      : wallet;
  }, [categories, walletSet, search, showAll]);

  const selectedLabel = (() => {
    if (!value) return null;
    const match = (categories || []).find((c) => c.value === value);
    return match ? match.label || match.value : value;
  })();

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-11 font-normal"
            data-testid={`${testid}-trigger`}
          >
            <span className={value ? '' : 'text-muted-foreground'}>
              {loading
                ? 'Loading categories…'
                : (selectedLabel || 'Pick a category')}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          sideOffset={4}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search categories…"
              value={search}
              onValueChange={setSearch}
              data-testid={`${testid}-search`}
            />
            <CommandList
              className="overscroll-contain"
              onWheelCapture={(e) => { e.stopPropagation(); }}
            >
              <CommandEmpty>
                {hasWallet && !showAll
                  ? 'No matches in your wallet. Toggle "Show all" to browse every canonical category.'
                  : 'No matching categories.'}
              </CommandEmpty>
              <CommandGroup
                heading={
                  showAll
                    ? `All canonical (${filtered.length})`
                    : `In your wallet (${filtered.length})`
                }
              >
                {filtered.map((c) => {
                  const isSelected = value === c.value;
                  return (
                    <CommandItem
                      key={c.value}
                      value={c.value}
                      onSelect={() => {
                        onChange(c.value);
                        setOpen(false);
                      }}
                      data-testid={`${testid}-option-${c.value}`}
                      className="cursor-pointer"
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100 text-primary' : 'opacity-0'}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {c.label || c.value}
                        </div>
                        {c.description && (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {c.description}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
            {hasWallet && (
              <div className="border-t border-border p-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {showAll ? 'Showing all canonical categories' : 'Showing wallet-relevant only'}
                </span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Switch
                    checked={showAll}
                    onCheckedChange={setShowAll}
                    data-testid={`${testid}-show-all-toggle`}
                  />
                  <span>Show all</span>
                </label>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default CategoryPicker;
